import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ContentPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  content: {
    title?: string;
    excerpt?: string;
    content?: string;
    description?: string;
    category?: string;
    imageUrl?: string;
    featured?: boolean;
  };
  type: 'blog' | 'portfolio';
}

const ContentPreview: React.FC<ContentPreviewProps> = ({
  isOpen,
  onClose,
  content,
  type,
}) => {
  if (!isOpen) return null;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const BlogPreview = () => (
    <article className="max-w-4xl mx-auto">
      {/* Hero Section */}
      {content.imageUrl && (
        <div className="relative h-64 md:h-96 mb-8 rounded-xl overflow-hidden">
          <img
            src={content.imageUrl}
            alt={content.title}
            className="w-full h-full object-cover"
          />
          {content.featured && (
            <div className="absolute top-4 left-4">
              <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold">
                Featured
              </span>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <header className="mb-8">
        {content.category && (
          <div className="mb-4">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {content.category}
            </span>
          </div>
        )}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {content.title || 'Untitled Blog Post'}
        </h1>
        {content.excerpt && (
          <p className="text-xl text-gray-600 leading-relaxed">
            {content.excerpt}
          </p>
        )}
        <div className="flex items-center text-gray-500 text-sm mt-4">
          <time>{formatDate(new Date())}</time>
          <span className="mx-2">•</span>
          <span>5 min read</span>
        </div>
      </header>

      {/* Content */}
      <div className="prose prose-lg max-w-none">
        {content.content ? (
          <div dangerouslySetInnerHTML={{ __html: content.content }} />
        ) : (
          <p className="text-gray-500 italic">No content yet...</p>
        )}
      </div>
    </article>
  );

  const PortfolioPreview = () => (
    <article className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
        <div>
          <div className="mb-6">
            {content.category && (
              <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                {content.category}
              </span>
            )}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {content.title || 'Untitled Portfolio Item'}
            </h1>
            {content.featured && (
              <div className="mb-4">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  ⭐ Featured Project
                </span>
              </div>
            )}
          </div>
        </div>
        
        {content.imageUrl && (
          <div className="relative">
            <img
              src={content.imageUrl}
              alt={content.title}
              className="w-full h-64 md:h-80 object-cover rounded-xl shadow-2xl"
            />
          </div>
        )}
      </div>

      {/* Description */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Overview</h2>
        <div className="prose prose-lg max-w-none">
          {content.description ? (
            <div dangerouslySetInnerHTML={{ __html: content.description }} />
          ) : (
            <p className="text-gray-500 italic">No description yet...</p>
          )}
        </div>
      </div>

      {/* Project Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div className="bg-gray-50 p-6 rounded-xl">
          <h3 className="font-semibold text-gray-900 mb-2">Timeline</h3>
          <p className="text-gray-600">{formatDate(new Date())}</p>
        </div>
        <div className="bg-gray-50 p-6 rounded-xl">
          <h3 className="font-semibold text-gray-900 mb-2">Category</h3>
          <p className="text-gray-600">{content.category || 'Uncategorized'}</p>
        </div>
        <div className="bg-gray-50 p-6 rounded-xl">
          <h3 className="font-semibold text-gray-900 mb-2">Status</h3>
          <p className="text-gray-600">In Development</p>
        </div>
      </div>
    </article>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <EyeIcon className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Preview: {type === 'blog' ? 'Blog Post' : 'Portfolio Item'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-8 bg-white">
          {type === 'blog' ? <BlogPreview /> : <PortfolioPreview />}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>This is how your content will appear to visitors</span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContentPreview;